// Local, AI-free recolor. Preserves source luminance so shadows, highlights,
// reflections, and wall imperfections stay intact. Runs in pure JS in <100ms
// for a 1280×800 image with several painted segments.
//
// Also provides finish simulation (matte → gloss) and lighting mood presets
// (natural / warm / cool / evening) so the browser preview approximates how
// paint reads under different rooms and times of day, without any AI hop.

export type PaintFinish = "matte" | "eggshell" | "satin" | "semi-gloss" | "gloss";
export type LightingMode = "natural" | "warm" | "cool" | "evening";

export const FINISH_LABELS: Record<PaintFinish, string> = {
  matte: "Matte",
  eggshell: "Eggshell",
  satin: "Satin",
  "semi-gloss": "Semi-gloss",
  gloss: "Gloss",
};

export const LIGHTING_LABELS: Record<LightingMode, string> = {
  natural: "Natural daylight",
  warm: "Warm incandescent",
  cool: "Cool daylight",
  evening: "Evening lamp",
};

// Highlight rolloff strength (0 = matte flat, 1 = mirror-like) per finish.
const FINISH_SHEEN: Record<PaintFinish, number> = {
  matte: 0.25,
  eggshell: 0.45,
  satin: 0.55,
  "semi-gloss": 0.72,
  gloss: 0.9,
};

function hexToRgb(hex: string): [number, number, number] {
  const v = hex.replace("#", "");
  const full = v.length === 3 ? v.split("").map((c) => c + c).join("") : v;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luma(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// RGB→HSL / HSL→RGB helpers (all channels 0..1). Used to re-hue a pixel
// while keeping its relative shading, which handles bright exterior siding
// where a pure luminance scale would wash the paint color out to near-white.
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
  else if (max === g) h = ((b - r) / d + 2);
  else h = ((r - g) / d + 4);
  return [h / 6, s, l];
}
function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1; if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) { const v = l * 255; return [v, v, v]; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    hue2rgb(p, q, h + 1 / 3) * 255,
    hue2rgb(p, q, h) * 255,
    hue2rgb(p, q, h - 1 / 3) * 255,
  ];
}

/**
 * Apply a paint color to `out` (already a copy of `src`) wherever `mask` is set.
 * Algorithm: replace the pixel's hue and saturation with the target color's,
 * and shift its lightness toward the target's lightness while keeping the
 * source's shading pattern (shadows, highlights, panel edges). This works on
 * both dark interior walls and bright exterior siding — a naive luminance
 * scale would blow bright siding to near-white and hide the color change.
 */
export function applyColorToBuffer(
  out: Uint8ClampedArray,
  src: Uint8ClampedArray,
  mask: Uint8ClampedArray,
  hex: string,
  finish: PaintFinish = "matte",
) {
  const [tr, tg, tb] = hexToRgb(hex);
  const [tH, tS, tL] = rgbToHsl(tr, tg, tb);
  const sheen = FINISH_SHEEN[finish] ?? FINISH_SHEEN.matte;
  // How strongly the painted pixel is pulled to the target lightness.
  // 1 = flat target color, 0 = keep source lightness entirely. A moderate
  // pull keeps the paint recognizable but preserves surface shading.
  const pull = 0.55;
  for (let i = 0, p = 0; i < mask.length; i++, p += 4) {
    const m = mask[i];
    if (!m) continue;
    const sr = src[p], sg = src[p + 1], sb = src[p + 2];
    const [, , sLn] = rgbToHsl(sr, sg, sb);
    // Blend source lightness toward the target lightness so the paint reads
    // clearly, but keep enough source variation to preserve shading.
    let newL = sLn * (1 - pull) + tL * pull;
    // Highlight rolloff — glossier finishes preserve more of the original
    // brightness in reflections; matte flattens them out.
    if (sLn > tL) {
      const hi = (sLn - tL) / Math.max(0.01, 1 - tL);
      newL = newL + (1 - newL) * hi * sheen * 0.6;
    }
    if (newL < 0) newL = 0; else if (newL > 1) newL = 1;
    const [r, g, b] = hslToRgb(tH, tS, newL);
    const a = m / 255;
    const inv = 1 - a;
    out[p]     = sr * inv + r * a;
    out[p + 1] = sg * inv + g * a;
    out[p + 2] = sb * inv + b * a;
    out[p + 3] = 255;
  }
}

/**
 * Post-pass tint applied to the whole buffer to simulate ambient light. The
 * shifts are subtle so painted color stays recognizable; they mirror what a
 * warm bulb / cool daylight / evening lamp does to a room photo.
 */
export function applyLighting(out: Uint8ClampedArray, mode: LightingMode) {
  if (mode === "natural") return;
  // [r-mult, g-mult, b-mult, brightness-add]
  const PRESETS: Record<Exclude<LightingMode, "natural">, [number, number, number, number]> = {
    warm:    [1.08, 1.02, 0.90,  4],
    cool:    [0.94, 0.99, 1.10, -2],
    evening: [1.05, 0.94, 0.82, -10],
  };
  const [mr, mg, mb, add] = PRESETS[mode];
  for (let p = 0; p < out.length; p += 4) {
    let r = out[p] * mr + add;
    let g = out[p + 1] * mg + add;
    let b = out[p + 2] * mb + add;
    if (r < 0) r = 0; else if (r > 255) r = 255;
    if (g < 0) g = 0; else if (g > 255) g = 255;
    if (b < 0) b = 0; else if (b > 255) b = 255;
    out[p] = r; out[p + 1] = g; out[p + 2] = b;
  }
}